<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="
          d-flex
          justify-content-between
          align-items-center
          content-sidebar-header
          px-2
          py-1
        "
      >
        <h5 class="mb-0 text-capitalize">
          {{ `${isAdd ? $t('actions.add') : $t('actions.edit')} ${ $tc('device_interphone.title', 1)}`}}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >

          <!-- device id -->
          <validation-provider
            #default="validationContext"
            name="device_id"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('device_interphone.device_id')"
              label-for="device_id"
            >
              <b-form-input
                id="device_id"
                v-model="itemData.device_id"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Unit -->
          <validation-provider #default="validationContext" name="Unit">
            <b-form-group
              class="text-capitalize"
              :label="$tc('unit.title', 2)"
              label-for="id_unit"
              :state="getValidationState(validationContext)"
            >
              <select-paginate
                v-model="itemData.units"
                :items="unitOptions"
                input-id="id_unit"
                :multiple="true"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- type device -->
          <validation-provider
            #default="validationContext"
            name="type_device_interphone"
            rules="required"
          >
            <b-form-group
              :label="$tc('type_device_interphone.title', 1)"
              label-for="type_device_interphone"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_type_device_interphone"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="typeDeviceInterphoneOptions"
                :reduce="(val) => val.value"
                input-id="type_device_interphone"
                :clearable="false"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- master account -->
          <validation-provider
            #default="validationContext"
            name="iotcomms_master_account"
            rules="required"
          >
            <b-form-group
              :label="$tc('iotcomms_master_account.title', 1)"
              label-for="iotcomms_master_account"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_iotcomms_master_account"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="iotcommsMasterAccountOptions"
                :reduce="(val) => val.value"
                input-id="iotcomms_master_account"
                :clearable="false"
                :disabled="!isAdd"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- center -->
          <validation-provider
            #default="validationContext"
            name="center"
          >
            <b-form-group
              :label="$tc('centers.title', 1)"
              label-for="center"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_center"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="centerOptions"
                :reduce="(val) => val.value"
                input-id="center"
                @input="changeCenter"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- sector -->
          <validation-provider
            #default="validationContext"
            name="sector"
          >
            <b-form-group
              :label="$tc('sectors.title', 1)"
              label-for="sector"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_sector"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="sectorOptions"
                :reduce="(val) => val.value"
                input-id="sector"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- user -->
          <validation-provider
            #default="validationContext"
            name="user"
          >
            <b-form-group
              :label="$tc('users.title', 1)"
              label-for="user"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_user"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="userOptions"
                :reduce="(val) => val.value"
                input-id="user"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- ivr -->
          <validation-provider
            name="ivr"
            rules="required"
          >
            <b-form-checkbox
              id="ivr"
              v-model="itemData.ivr"
              name="ivr"
              value="1"
              unchecked-value="0"
              class="my-1 text-capitalize"
              switch
            >
              {{ $t("device_interphone.ivr") }}
            </b-form-checkbox>
          </validation-provider>

          <!-- alarm -->
          <validation-provider
            name="alarm"
            rules="required"
            vid="alarm"
          >
            <b-form-checkbox
              id="alarm"
              v-model="itemData.alarm"
              name="alarm"
              value="1"
              unchecked-value="0"
              class="my-1 text-capitalize"
              switch
            >
              {{ $t("device_interphone.alarm") }}
            </b-form-checkbox>
          </validation-provider>

          <!-- tipo alarma -->
          <validation-provider
            #default="validationContext"
            name="id_type_alarm"
            rules="required_if:alarm,1"
            v-if="itemData.alarm == 1"
          >
            <b-form-group
              class="text-capitalize"
              :label="$tc('types_alarms.title', 1)"
              label-for="id_type_alarm"
              :state="getValidationState(validationContext)">
              <v-select
                v-model="itemData.id_type_alarm"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="typeAlarmOptions"
                :reduce="(val) => val.value"
                input-id="id_type_alarm"
              />
              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t('actions.add') : $t('actions.edit') }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t('actions.cancel') }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import { ref } from '@vue/composition-api'
import { required, alphaNum, email, positive, required_if } from '@validations'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent
  from '@core/components/toastification/ToastificationContent.vue'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import SelectPaginate from "../../components/SelectPaginate.vue"

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,

    // Form Validation
    ValidationProvider,
    ValidationObserver,

    SelectPaginate,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isSidebarActive',
    event: 'update:is-sidebar-active',
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    centerOptions: {
      type: Array,
      required: true,
    },
    sectorOptions: {
      type: Array,
      required: true,
    },
    typeAlarmOptions: {
      type: Array,
      required: true,
    },
    typeDeviceInterphoneOptions: {
      type: Array,
      required: true,
    },
    iotcommsMasterAccountOptions: {
      type: Array,
      required: true,
    },
    userOptions: {
      type: Array,
      required: true,
    },
    unitOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    }
  },
  setup(props, { emit }) {
    const blankData = {
      device_id: '',
      id_center: null,
      id_sector: null,
      id_type_alarm: null,
      id_type_device_interphone: null,
      id_iotcomms_master_account: null,
      id_user: null,
      alarm: 0,
      ivr: 0,
      units: null
    }

    const toast = useToast()

    const itemData = ref(JSON.parse(JSON.stringify(blankData)))
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData))
    }

    const onSubmit = () => {
      let req = itemData.value

      req.units = itemData.value.units?.length > 0
                    ? itemData.value.units.map((unit) => unit.value)
                    : null

      let dispatch

      if (props.isAdd) {
        dispatch = store.dispatch('device_interphone/add', req)
      } else {
        const data = {
          id: itemData.value.id,
          params: req,
        }

        dispatch = store.dispatch('device_interphone/edit', data)
      }

      dispatch
        .then((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          emit('refetch-data')
          emit('update:is-sidebar-active', false)
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }

    const changeCenter = () => {
      store
        .dispatch("sector/getAll", {
          id_center: [itemData.value.id_center],
        })
        .then(() => {
          const sector = props.sectorOptions.filter(
            (item) => item.value == itemData.value.id_sector
          )

          if (sector.length == 0) itemData.value.id_sector = ""
        })
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm
    } = formValidation(resetData)

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = props.itemEdit
        itemData.value.id_center = props.itemEdit.center?.id
        itemData.value.id_sector = props.itemEdit.sector?.id
        itemData.value.id_type_alarm = props.itemEdit.typeAlarm?.id
        itemData.value.id_type_device_interphone = props.itemEdit.typeDeviceInterphone.id
        itemData.value.id_iotcomms_master_account = props.itemEdit.iotcommsMasterAccount.id
        itemData.value.id_user = props.itemEdit.user?.id

        itemData.value.units = props.itemEdit.device?.units.map((value) => ({
                                                                  value: value.id,
                                                                  label: value.description,
                                                                }))

        changeCenter()
      }
    }

    return {
      itemData,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,

      changeCenter
    }
  },
}
</script>